import React, { useEffect, useMemo, useRef, useState } from "react";
import CustomTable from "../CustomTable";
import CustomButton from "../CustomButton";
import moment from "moment";
import { icons } from "../../assets/icons";
import DatePicker from "../FormFields/DatePicker";
import useApi from "../../hooks/useApi";
import { apiUrls } from "../../utils/api/apiUrls";
import {
  displayDateFormat,
  downloadReports,
  downloadReportsFun,
  getBase64,
  sendToDBDateFormat,
} from "../../utils/helpers";
import { toast } from "react-toastify";
import PreviewDocModal from "../../pages/cashDeposits/PreviewDocModal";
import {
  useNavigate,
  createSearchParams,
  useSearchParams,
} from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

const SavedVouchers = () => {
  const { data = [], nonProcessedData = [], apiCall } = useApi();
  const { apiCall: sendForApproval } = useApi();
  const { apiCall: fileDownloadAtStore } = useApi();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [startDate, setStartDate] = useState(
    moment(
      searchParams.get("startDate") || moment().subtract({ days: 7 }).toDate()
    ).toDate()
  );
  const [endDate, setEndDate] = useState(
    moment(searchParams.get("endDate") || new Date()).toDate()
  );
  const { apiCall: fileDownload } = useApi();
  const { data: dataDelete, apiCall: DeleteVoucher } = useApi();
  const [selectedCheckBoxes, setSelectedCheckBoxes] = useState([]);
  const [downloadPreviewOpenWithData, setDownloadPreviewOpenWithData] =
    useState(false);
  const getRecordsInFlightRef = useRef(false);
  const lastFetchKeyRef = useRef("");
  const {
    selectedStores = {},
    storeDropdownState,
    user: { userLabel = "", username = "" },
  } = useAuth();
  const { storeCode = "" } = selectedStores?.storeDetails || {};

  const savedVouchersApiCall = (start, end) => {
    const safeStart = start || new Date();
    const safeEnd = end || safeStart;
    const requestKey = JSON.stringify({
      startDate: moment(safeStart).startOf("d").format("YYYY-MM-DD HH:mm:ss"),
      endDate: moment(safeEnd).endOf("d").format("YYYY-MM-DD HH:mm:ss"),
      metadataSchemaId: 4,
      status: "SAVED",
    });
    if (
      getRecordsInFlightRef.current &&
      lastFetchKeyRef.current === requestKey
    ) {
      return;
    }
    getRecordsInFlightRef.current = true;
    lastFetchKeyRef.current = requestKey;

    navigate(
      {
        pathname: window.location.pathname,
        search: createSearchParams({
          startDate: safeStart,
          endDate: safeEnd,
        }).toString(),
      },
      { replace: true }
    );
    // if(storeCode.length > 0){
    apiCall({
      url: apiUrls.dmsService.getRecords,
      method: "post",
      dataToSend: {
        startDate: moment(safeStart).startOf("d").format("YYYY-MM-DD HH:mm:ss"),
        metadataSchemaId: 4,
        endDate: moment(safeEnd)
          .endOf("d")
          .format("YYYY-MM-DD HH:mm:ss"),
        status: "SAVED",
        searchText: "",
      },
      onSuccess: () => {
        getRecordsInFlightRef.current = false;
      },
      onFail: () => {
        getRecordsInFlightRef.current = false;
      },
      onError: () => {
        getRecordsInFlightRef.current = false;
      },
      processData: true,
      accessor: "storeGetResponse",
      itemAccessor: "dataModalRecordsResponse",
    });
    // }
  };

  const storeFetchDependencyKey = useMemo(() => {
    const selectedCode =
      selectedStores?.storeDetails?.storeCode ||
      selectedStores?.storeCode ||
      selectedStores?.value ||
      "";
    const dropdownCount = Array.isArray(storeDropdownState)
      ? storeDropdownState.length
      : storeDropdownState
      ? 1
      : 0;
    return `${selectedCode}|${dropdownCount}`;
  }, [
    selectedStores?.storeDetails?.storeCode,
    selectedStores?.storeCode,
    selectedStores?.value,
    Array.isArray(storeDropdownState) ? storeDropdownState.length : 0,
  ]);

  useEffect(() => {
    savedVouchersApiCall(startDate, endDate);
  }, [storeFetchDependencyKey]);

  const onClickDownloadFile = (file_id, filename) => {
    fileDownload({
      url: apiUrls.dmsService.getFile + "/" + file_id,
      method: "get",
      responseType: "blob",
      onSuccess: (apiData) => {
        const url = window.URL.createObjectURL(new Blob([apiData.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      },
    });
  };

  const onClickViewFile = (file_id, filename) => {
    fileDownload({
      url: apiUrls.dmsService.getFile + "/" + file_id,
      method: "get",
      responseType: "blob",
      onSuccess: (apiData) => {
        setDownloadPreviewOpenWithData({
          file: apiData.data,
          filename,
          isOpen: true,
          file_id: file_id,
        });
      },
    });
  };

  const amountToTotal = (val) => {
    return val === "amount" ? "total_amount" : val;
  };

  const columns = [
    {
      Header: "Date",
      accessor: "date",
      Cell: (props) => {
        return (
          <div>
            {moment(props?.row?.original?.date).format(displayDateFormat)}
          </div>
        );
      },
    },
    {
      Header: "Voucher Head",
      accessor: "head",
      Cell: (props) => {
        const value = props?.row?.original;

        return (
          <div className="py-4">
            <div className="font-size-small">{value?.head}</div>
            {/* <div className="text-[1.4rem] text-[var(--ultraLightGray)]">
              {value?.sub_head}
            </div> */}
          </div>
        );
      },
    },
    {
      Header: "Amount (₹)",
      accessor: "amount",
      Cell: (props) => {
        const value = props?.row?.original?.amount;
        let amount;
        if (typeof value == "string") {
          amount = Number(value).toFixed(2);
        } else {
          amount = value;
        }

        return (
          <div className="py-4">
            <div className="font-size-small">{amount}</div>
            {/* <div className="text-[1.4rem] text-[var(--ultraLightGray)]">
              {value?.sub_head}
            </div> */}
          </div>
        );
      },
    },
    {
      Header: "Voucher Comment",
      accessor: "comment",
      Cell: (props) => {
        const value = props?.row?.original;

        return (
          <div className="py-4">
            <div className="font-size-small">{value?.comment}</div>
            {/* <div className="text-[1.4rem] text-[var(--ultraLightGray)]">
              {value?.sub_head}
            </div> */}
          </div>
        );
      },
    },
    {
      Header: "",
      accessor: "file_id",
      Cell: (props) => {
        if (!props?.row?.original?.filename) {
          return null;
        }

        return (
          <div className="flex">
            <img
              src={icons.squareEyeIcon}
              alt="view icon"
              className="cursor-pointer"
              onClick={() => {
                onClickViewFile(
                  props?.row?.original?.file_id,
                  props?.row?.original?.filename
                );
              }}
            />
            <img
              src={icons.downloadIcon}
              alt="attachment icon"
              className="w-[2.1rem] h-[1.9rem] cursor-pointer ml-4"
              onClick={() => {
                onClickDownloadFile(
                  props?.row?.original?.file_id,
                  props?.row?.original?.filename
                );
              }}
            />
            <img
              src={icons.trashIcon}
              alt="attachment icon"
              className="w-[2.1rem] h-[1.9rem] cursor-pointer ml-4"
              onClick={() =>
                // onClickDownloadFile(
                //   props?.row?.original?.file_id,
                //   props?.row?.original?.filename
                // );
                deleteVoucher(props?.row?.original?.file_id)
              }
            />
          </div>
        );
      },
    },
  ];

  const onClickSendForApprove = async () => {
    const voucherDetails =
      nonProcessedData?.storeGetResponse?.[0]?.dataModalRecordsResponse;
    const values = {
      id: voucherDetails?.id,
      [amountToTotal(
        voucherDetails?.dataModalAttributeSet?.[0]?.metadataTechnicalName
      )]: voucherDetails?.dataModalAttributeSet?.[0]?.attributeValue,
      [amountToTotal(
        voucherDetails?.dataModalAttributeSet?.[1]?.metadataTechnicalName
      )]: voucherDetails?.dataModalAttributeSet?.[1]?.attributeValue,
      documents: (voucherDetails?.documents || []).map((item) => {
        let obj = {};
        item?.dataModalAttributeSet?.forEach((dataSet) => {
          obj[dataSet?.metadataTechnicalName] = {
            value:
              dataSet?.metadataTechnicalName === "date"
                ? moment(dataSet?.attributeValue).toDate()
                : dataSet?.attributeValue,
            id: item?.id,
          };
        });
        obj.id = item?.id;
        if (item?.documentName) {
          obj.documentName = item?.documentName;
        }
        if (item?.documentPath) {
          obj.documentPath = item?.documentPath;
        }
        if (item?.documentPath) {
          obj.file = { value: item?.documentPath };
        }
        return obj;
      }),
    };

    const documents = await Promise.all(
      values?.documents.map(async (item) => {
        let fileData = {};
        if (item?.file?.value?.[0].name) {
          var filename = item?.file?.value[0].name;
          let file = item?.file?.value[0];
          const base64 = await getBase64(file);
          fileData = {
            document: base64.split(",")[1],
            documentName: filename,
          };
        }

        let returnObj = {
          dataModalAttributeSet: Object.keys(item)
            .map((key) => {
              if (
                [
                  "file",
                  "id",
                  "documentName",
                  "documentPath",
                  "approval_status",
                ].includes(key)
              ) {
                return null;
              }
              if (key === "date") {
                let obj = {
                  attributeValue: moment(item?.[key]?.value).format(
                    sendToDBDateFormat
                  ),
                  metadataTechnicalName: key,
                };

                if (item?.[key]?.id) {
                  let idd = voucherDetails?.documents
                    ?.find((it) => it?.id === item?.[key]?.id)
                    ?.dataModalAttributeSet?.find(
                      (it) => it?.metadataTechnicalName === key
                    )?.id;
                  if (idd) {
                    obj.id = idd;
                  }
                }

                return obj;
              } else {
                let obj = {
                  attributeValue: item?.[key]?.value,
                  metadataTechnicalName: key,
                };
                if (item?.[key]?.id) {
                  let idd = voucherDetails?.documents
                    ?.find((it) => it?.id === item?.[key]?.id)
                    ?.dataModalAttributeSet?.find(
                      (it) => it?.metadataTechnicalName === key
                    )?.id;

                  if (idd) {
                    obj.id = idd;
                  }
                }

                return obj;
              }
            })
            .filter((it) => it !== null),
          ...fileData,
          metadataSchemaId: 5,
        };

        if (item.id) {
          returnObj.id = item?.id;
        }
        if (item.documentPath) {
          returnObj.documentPath = item?.documentPath;
        }
        if (item.documentName) {
          returnObj.documentName = item?.documentName;
        }

        return returnObj;
      })
    );

    let dataToSend = {
      dataModalAttributeSet: [
        {
          attributeValue: values.description,
          metadataTechnicalName: "description",
          id: voucherDetails?.dataModalAttributeSet?.find(
            (it) => it?.metadataTechnicalName === "description"
          )?.id,
        },
        {
          attributeValue: values.total_amount?.toString(),
          metadataTechnicalName: "amount",
          id: voucherDetails?.dataModalAttributeSet?.find(
            (it) => it?.metadataTechnicalName === "amount"
          )?.id,
        },
      ],
      documents,
      metadataSchemaId: 4,
    };

    if (voucherDetails?.id) {
      dataToSend.id = voucherDetails?.id;
    }
    sendForApproval({
      url: apiUrls.dmsService.sendForApproval,
      dataToSend,
      method: "post",
      onSuccess: () => {
        toast.success("Successfully sent for approval");
        savedVouchersApiCall(startDate, endDate);
      },
    });
  };

  const deleteVoucher = (voucher_id) => {
    DeleteVoucher({
      url: apiUrls.dmsService.deleteVoucher + "/" + voucher_id,
      method: "delete",
      onSuccess: () => {
        toast.success("Successfully Delete Voucher");
        savedVouchersApiCall(startDate, endDate);
      },
    });
  };

  const onChangeDate = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
    if (end) savedVouchersApiCall(start, end);
  };

  const memoColumns = useMemo(() => columns, []);

  // const spreadStoreData = (arr) => {
  //   let d = arr?.map(i => {
  //     return (
  //       [...i.documents]
  //     )
  //   });
  //   if(d[0]?.length > 0){
  //     return [...d[0]]
  //   } else {
  //     return arr || []
  //   }
  // }

  // const spreadStoreData =

  const onClickSingleDownloadFile = () => {
    // const st =`${moment(startDate).format('YYYY-MM-DD')} 00:00:00`;
    // const et = `${moment(endDate).format('YYYY-MM-DD')} 23:59:59`;
    const st = `${moment(startDate).format("YYYY-MM-DD")} 00:00:00`;
    const et = `${moment(endDate).format("YYYY-MM-DD")} 23:59:59`;
    fileDownloadAtStore({
      url: `${apiUrls.dmsService.downloadVoucherStoreLevel}`,
      dataToSend: {
        storeCodes: [username],
        status: "Saved",
        metadataSchemaId: 4,
        endDate: et,
        startDate: st,
      },
      method: "post",
      responseType: "blob",
      responseData: (res) => {
        const { data = "", status = null } = res || {};
        const contentType = res?.headers?.["content-type"];
        const contentDisposition = res?.headers["content-disposition"];
        const fileNameMatch =
          contentDisposition && contentDisposition.match(/filename="(.+)"/);
        const fileName = fileNameMatch
          ? fileNameMatch[1]
          : "downloaded-file.xlsx";
        if (status === 200) {
          toast.success("Download File Successfully");
          downloadReportsFun(data, "AllVoucher.xlsx", contentType);
        } else {
          toast.success("Something Went Wrong");
        }
      },
    });
  };

  return (
    <div className="flex flex-col py-2">
      <div className="flex justify-end mt-2 mb-8">
        <div className="flex items-center gap-x-3">
          <div className="font-size-medium">Date Range</div>

          <DatePicker
            startDate={startDate}
            endDate={endDate}
            onChange={onChangeDate}
            maxDate={new Date()}
            dateRange
          />
        </div>
      </div>
      <CustomTable
        // data={React.memo( () => data?.flatMap(obj => obj.documents), []) || []}
        data={
          React.useMemo(() => data?.flatMap((obj) => obj.documents), [data]) ||
          []
        }
        columns={memoColumns}
        showCheckboxes={true}
        onCheckboxSelect={(selectedCheckBoxes) => {
          const data1 = data?.[0]?.documents || [];
          if (data1?.length) {
            setSelectedCheckBoxes(selectedCheckBoxes);
          }
        }}
        centerAlignedColumns={["amount"]}
        // isGlobalSearchEnable
        // globalSearchPosition={{
        //   top: "-63px",
        //   left: 0,
        // }}
      />
      <div className="flex items-center self-end mt-5 gap-x-6">
        <CustomButton
          title="Edit Voucher"
          wrapperClassName={`!px-10 ${
            selectedCheckBoxes?.length > 0 ? "" : "disabled-kv"
          }`}
          onClick={() => {
            if (data?.[0]?.vid) {
              navigate("/vouchers/edit", { state: { isEditFeatureOn: true } });
            }
          }}
        />
        {/* <CustomButton
          title="Delete"
          wrapperClassName={`!px-10 ${data?.[0]?.vid ? "" : "disabled-kv"}`}
          onClick={deleteVoucher}
        /> */}
        <CustomButton
          title="Download"
          wrapperClassName={`!px-10 ${
            selectedCheckBoxes?.length > 0 ? "" : "disabled-kv"
          }`}
          onClick={() => {
            onClickSingleDownloadFile();
            // let reportSettings = {
            //   fileName: "Saved Vouchers", // Name of the resulting spreadsheet
            //   extraLength: 3, // A bigger number means that columns will be wider
            //   writeMode: "writeFile", // The available parameters are 'WriteFile' and 'write'. This setting is optional. Useful in such cases https://docs.sheetjs.com/docs/solutions/output#example-remote-file
            //   writeOptions: {}, // Style options from https://docs.sheetjs.com/docs/api/write-options
            //   RTL: false, // Display the columns from right-to-left (the default value is false)
            // };

            // if (data?.[0]?.documents?.length > 0) {
            //   downloadReports(
            //     [
            //       {
            //         sheet: "Saved Vouchers",
            //         columns: [
            //           {
            //             label: "Date",
            //             value: (row) => row.date,
            //           }, // Top level data
            //           {
            //             label: "Head",
            //             value: (row) => row.head,
            //           }, // Top level data
            //           { label: "Amount", value: (row) => row.amount }, // Top level data
            //           {
            //             label: "Comment",
            //             value: (row) => row?.comment || "-",
            //           }, // Top level data
            //         ],
            //         content: selectedCheckBoxes?.map((item) => item?.original),
            //       },
            //     ],
            //     reportSettings
            //   );
            // }
          }}
        />
        {/* <CustomButton
          title="Send For Approval"
          wrapperClassName={`!px-10 ${data?.[0]?.vid ? "" : "disabled-kv"}`}
          onClick={onClickSendForApprove}
        /> */}
      </div>
      <PreviewDocModal
        isModalVisible={downloadPreviewOpenWithData}
        setIsModalVisible={setDownloadPreviewOpenWithData}
      />
    </div>
  );
};

export default SavedVouchers;
